import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary-action text-primary-foreground shadow-action hover:bg-primary-action/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-control hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-control hover:bg-primary-action hover:text-primary-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-control hover:bg-secondary/80",
        ghost: "hover:bg-primary-action hover:text-primary-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-control px-3 text-xs",
        lg: "h-10 rounded-control px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export interface IconButtonProps
  extends Omit<ButtonProps, "size" | "aria-label"> {
  /** Accessible name for the icon-only action. Also exposed as a native hint. */
  label: string
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, title, children, ...props }, ref) => (
    <Button
      ref={ref}
      size="icon"
      aria-label={label}
      title={title ?? label}
      {...props}
    >
      {children}
    </Button>
  )
)
IconButton.displayName = "IconButton"

export interface LoadingButtonProps extends ButtonProps {
  isLoading?: boolean
  loadingLabel?: string
}

const LoadingButton = React.forwardRef<HTMLButtonElement, LoadingButtonProps>(
  ({ isLoading = false, loadingLabel, disabled, children, ...props }, ref) => (
    <Button
      ref={ref}
      aria-busy={isLoading || undefined}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
      {isLoading && loadingLabel ? loadingLabel : children}
    </Button>
  )
)
LoadingButton.displayName = "LoadingButton"

export { Button, IconButton, LoadingButton, buttonVariants }
